import { format as fnsFormat, formatDistanceToNow } from "date-fns"
import { enUS, es } from "date-fns/locale"

import i18n from "@/i18n"
import type { GradeScale } from "@/types"

const dateFnsLocale = () => (i18n.language.startsWith("es") ? es : enUS)

/** `YYYY-MM-DD` with no time part — a calendar day, not an instant. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

export function formatDate(d: Date | string, pattern = "PP") {
  // A date-only string parses as UTC midnight per the ECMAScript spec,
  // so west of Greenwich `new Date("2026-08-10")` renders as Aug 9 —
  // a professor who scheduled a send for the 10th read "9 ago" on the
  // card. Date-only values carry no timezone; pin them to local
  // midnight so the day survives formatting. Full ISO timestamps
  // (created_at, sent_at, …) are real instants and keep their existing
  // behavior. Callers that already work around this by hand build
  // `${iso}T00:00:00` themselves and pass a Date, which skips this path.
  const date = typeof d === "string" ? new Date(DATE_ONLY.test(d) ? `${d}T00:00:00` : d) : d
  return fnsFormat(date, pattern, { locale: dateFnsLocale() })
}

/**
 * "Today" anchored to America/Mexico_City, not the browser's local clock.
 * The backend computes scheduling floors (PRD-25 §FR-Schedule-Window) via
 * Django's `timezone.now()`, which is pinned to America/Mexico_City
 * (`platonica/settings/base.py`). A professor on a browser in a different
 * timezone must see the same "today" the backend enforces — otherwise the
 * SPA's `min` date attribute and the backend's `material_send_date_too_soon`
 * check can disagree near midnight MX time.
 *
 * Returns a local-midnight `Date` (browser timezone, but with the correct
 * Y/M/D per Mexico City) so downstream `setDate` arithmetic and `isoDate()`
 * formatting stay simple. Mexico's mainland has not observed DST since 2022
 * (only northern border municipalities do) — that residual edge case is an
 * accepted risk elsewhere in this codebase and isn't re-litigated here.
 */
export function getMexicoCityToday(): Date {
  return new Date(`${mexicoCityDay()}T00:00:00`)
}

/**
 * The `YYYY-MM-DD` calendar day `instant` falls on in America/Mexico_City
 * (now, by default). The string form of {@link getMexicoCityToday}, for
 * comparing against the date-only fields the backend sends (a reopen's
 * `submission_reopened_through`) and for naming the day an instant belongs
 * to whatever the device's own clock says.
 */
export function mexicoCityDay(instant: Date = new Date()): string {
  const { y, m, d } = mexicoCityParts(instant)
  return `${y}-${m}-${d}`
}

/**
 * `instant` as Mexico City's wall clock reads it, returned as a local Date
 * built for FORMATTING only (never for arithmetic or for sending back): its
 * fields are Mexico City's, whatever zone the device is in. For a sentence a
 * student reads as "hasta el viernes 6 de noviembre a las 11:59 p.m." about
 * a window the server closes on Mexico City's calendar (PRD-73 §FR-10).
 */
export function mexicoCityWallClock(instant: Date): Date {
  const { y, m, d, hh, mm, ss } = mexicoCityParts(instant)
  return new Date(`${y}-${m}-${d}T${hh}:${mm}:${ss}`)
}

function mexicoCityParts(instant: Date) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value
  return {
    y: part("year"),
    m: part("month"),
    d: part("day"),
    hh: part("hour"),
    mm: part("minute"),
    ss: part("second"),
  }
}

export function formatDateRelative(d: Date | string) {
  const date = typeof d === "string" ? new Date(d) : d
  return formatDistanceToNow(date, { locale: dateFnsLocale(), addSuffix: true })
}

// PRD-09: Alias of `formatDateRelative` for the new student detail page —
// keeps the call-site phrasing ("hace 2 días") explicit and gives us a place
// to swap in `Intl.RelativeTimeFormat` later if we ever drop date-fns.
export function formatRelativeDate(d: Date | string) {
  return formatDateRelative(d)
}

export function formatNumber(n: number, opts?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(i18n.language, opts).format(n)
}

/**
 * Format a grade for display. Scale-aware: 0-10 keeps one decimal
 * ("8.7"), 0-100 renders an integer ("87"). The `scale` parameter is
 * optional and defaults to "0-10" so call sites without scale context
 * keep their historical behaviour — but every surface that knows the
 * professor's active scale (via `useGradeScale()`) should pass it.
 *
 * Locale-aware via `formatNumber` — Spanish renders "8,7", English
 * "8.7".
 */
export function formatGrade(n: number, scale: GradeScale = "0-10") {
  if (scale === "0-100") {
    return formatNumber(Math.round(n), { maximumFractionDigits: 0 })
  }
  return formatNumber(n, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

/** Accepts a 0-100 percentage; formats per active locale. */
export function formatPercent(percent: number) {
  return formatNumber(percent / 100, { style: "percent", maximumFractionDigits: 0 })
}

/**
 * Compact "hace 5min / 2h / 3d" relative-time format. Used where
 * date-fns's verbose "hace alrededor de 1 hora" doesn't fit — small
 * captions in dense grids (per-student email cards, etc.). Falls
 * back to absolute "5 may" once the relative form exceeds ~30 days.
 *
 * Why not just date-fns `formatDistanceToNow`: it inserts approximation
 * filler ("alrededor de", "más de") that's load-bearing for prose but
 * line-wraps a small caption.
 */
export function formatRelativeShort(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  const isEs = i18n.language.startsWith("es")
  if (seconds < 60) return isEs ? "ahora" : "now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return isEs ? `hace ${minutes}min` : `${minutes}min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return isEs ? `hace ${hours}h` : `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return isEs ? `hace ${days}d` : `${days}d ago`
  return formatDate(date, "d MMM")
}

/** "2.4 MB" / "640 KB" — one decimal above a megabyte, none below. */
export function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${mb.toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** "fotos, PDF o Word" — the locale's own list grammar, never a hand-joined ", o ". */
export function formatList(items: string[], type: "conjunction" | "disjunction" = "conjunction") {
  const locale = document.documentElement.lang || "es-MX"
  return new Intl.ListFormat(locale, { style: "long", type }).format(items)
}
